import { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { observer } from 'mobx-react';
import { VideoView } from 'expo-video';
import { router } from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import Ionicons from '@expo/vector-icons/Ionicons';

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';
import { useStores } from '@/stores/StoreProvider';
import { colors } from '@/constants/colors';
import GuidedWorkoutPlayer from '@/components/GuidedWorkoutPlayer';
import DefaultBackground from '@/components/DefaultBackground';
import CountdownOverlay from '@/components/CountdownOverlay';
import { IWorkoutPlayerStore } from '@/stores/workoutPlayer/WorkoutPlayerStore';
import { useCardPlayerController } from '@/hooks/useCardPlayerController';
import { autorun } from 'mobx';

const COUNTDOWN_ENABLED = true;

// Card-based workout player
const CardWorkoutPlayer = observer(
  ({
    store,
    autoFocusPause,
  }: {
    store: IWorkoutPlayerStore;
    autoFocusPause: boolean;
  }) => {
    const scale = useScale();
    const styles = useCardStyles();
    const { player } = useCardPlayerController(store);
    const hasVideo = !!store.currentVideoUrl;

    return (
      <View style={styles.playerContainer}>
        {/* Video area — always centered */}
        <View style={styles.contentArea}>
          <View style={styles.videoWrapper}>
            {hasVideo ? (
              <VideoView
                player={player}
                style={styles.video}
                nativeControls={false}
                contentFit="contain"
              />
            ) : (
              <View style={[styles.video, styles.noVideoPlaceholder]}>
                <ThemedText style={styles.placeholderText}>
                  {store.isRestSegment ? 'Rest' : 'No Video'}
                </ThemedText>
              </View>
            )}
          </View>
        </View>

        {/* Info below video */}
        <View style={styles.infoArea}>
          {store.isChangePhase && (
            <ThemedText style={styles.changeSidesText}>Switch Sides</ThemedText>
          )}
          {!!store.repsOrTimeLabel && (
            <ThemedText style={styles.repsTimeLabel}>
              {store.repsOrTimeLabel}
            </ThemedText>
          )}
          {!!store.nextUpTitle && (
            <ThemedText style={styles.nextUpText}>
              {store.nextUpTitle}
            </ThemedText>
          )}
          <ThemedText style={styles.progressText}>{store.progress}</ThemedText>
        </View>

        {/* Bottom controls */}
        <View style={styles.bottomBar}>
          <View style={styles.controls}>
            <Pressable
              // We use a combination of key and dynamic hasTVPreferredFocus to force a re-mount of the component
              // when we need it to be ensure the preferred focus behavior.
              // It seems that our countdown overlay invalidates the preferred focus on the first mount since
              // it covers everything. But if we force a re-mount after the countdown is done, the focus works as expected.
              key={autoFocusPause ? 'pause-focus' : 'pause-no-focus'}
              hasTVPreferredFocus={autoFocusPause}
              style={({ focused }) => [
                styles.controlButton,
                styles.controlButtonLarge,
                focused && styles.controlButtonFocused,
              ]}
              onPress={() => store.togglePause()}
            >
              <Ionicons
                name={store.isPaused ? 'play' : 'pause'}
                size={28 * scale}
                color="#fff"
              />
            </Pressable>

            <Pressable
              style={({ focused }) => [
                styles.controlButton,
                focused && styles.controlButtonFocused,
              ]}
              onPress={() => store.skipStep()}
            >
              <Ionicons
                name="play-skip-forward"
                size={22 * scale}
                color="#fff"
              />
            </Pressable>
          </View>
        </View>
      </View>
    );
  },
);

function PlayerScreen() {
  const {
    workoutPlayerStore,
    guidedWorkoutPlayerStore,
    workoutSessionStore,
  } = useStores();
  const styles = usePlayerStyles();
  const [showCountdown, setShowCountdown] = useState(COUNTDOWN_ENABLED);
  // Gate the native media surfaces on focus: when the screen is mounted but
  // unfocused (e.g. another route is pushed on top), unmount so expo-video
  // releases its native player.
  const isFocused = useIsFocused();

  // The guided player owns its own Back overlay + title, so the shared top bar
  // is hidden for it (lets the video use the full screen height).
  const hideTopBar = workoutSessionStore.mode === 'guided';

  const handleBack = useCallback(() => {
    workoutSessionStore.reset();
    router.replace('/(main)');
  }, [workoutSessionStore]);

  const handleCountdownComplete = useCallback(() => {
    setShowCountdown(false);
    if (workoutSessionStore.mode === 'card') {
      workoutPlayerStore.startPlayback();
    } else if (workoutSessionStore.mode === 'guided') {
      guidedWorkoutPlayerStore.startPlayback();
    }
  }, [workoutSessionStore, workoutPlayerStore, guidedWorkoutPlayerStore]);

  // TEMP: When countdown is disabled, start the workout immediately so the
  // behavior matches the "countdown finished" state. Remove this block when
  // re-enabling the countdown.
  useEffect(() => {
    if (COUNTDOWN_ENABLED) return;
    handleCountdownComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for workout completion and navigate to complete screen
  useEffect(() => {
    const dispose = autorun(() => {
      const isFinished =
        (workoutSessionStore.mode === 'card' &&
          workoutPlayerStore.isFinished) ||
        (workoutSessionStore.mode === 'guided' &&
          guidedWorkoutPlayerStore.isFinished);

      if (isFinished) {
        router.replace('/(workout)/complete');
      }
    });

    return dispose;
  }, [workoutPlayerStore, guidedWorkoutPlayerStore, workoutSessionStore]);

  // If no active workout, redirect back
  useEffect(() => {
    if (workoutSessionStore.mode === 'none') {
      router.replace('/(main)');
    }
  }, [workoutSessionStore.mode]);

  if (workoutSessionStore.mode === 'none') {
    return null;
  }

  return (
    <View style={styles.container}>
      <DefaultBackground />
      {!hideTopBar && (
        <View style={styles.topBar}>
          <Pressable
            style={({ focused }) => [
              styles.backButton,
              focused && styles.backButtonFocused,
            ]}
            onPress={handleBack}
          >
            <ThemedText style={styles.backButtonText}>Back</ThemedText>
          </Pressable>
          <View style={styles.topBarCenter}>
            {!!workoutPlayerStore.segmentLabel && (
              <ThemedText style={styles.topBarTitle} numberOfLines={1}>
                {workoutPlayerStore.segmentLabel}
              </ThemedText>
            )}
            {!!workoutPlayerStore.groupLabel && (
              <ThemedText style={styles.topBarSubtitle}>
                {workoutPlayerStore.groupLabel}
              </ThemedText>
            )}
          </View>
        </View>
      )}
      {isFocused && workoutSessionStore.mode === 'card' && (
        <CardWorkoutPlayer
          store={workoutPlayerStore}
          autoFocusPause={!showCountdown}
        />
      )}
      {isFocused && workoutSessionStore.mode === 'guided' && (
        <GuidedWorkoutPlayer
          store={guidedWorkoutPlayerStore}
          autoFocusPause={!showCountdown}
          onBack={handleBack}
        />
      )}
      {isFocused && showCountdown && COUNTDOWN_ENABLED && (
        <CountdownOverlay onComplete={handleCountdownComplete} />
      )}
    </View>
  );
}

export default observer(PlayerScreen);

function usePlayerStyles() {
  const scale = useScale();

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.brand.primary,
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 24 * scale,
      paddingHorizontal: 24 * scale,
    },
    topBarCenter: {
      alignItems: 'center',
    },
    backButton: {
      position: 'absolute',
      left: 24 * scale,
      paddingVertical: 8 * scale,
      paddingHorizontal: 20 * scale,
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: 20 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    backButtonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    backButtonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    topBarTitle: {
      color: '#fff',
      fontSize: 20 * scale,
      fontWeight: '700',
      textAlign: 'center',
    },
    topBarSubtitle: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 14 * scale,
      marginTop: 2 * scale,
      textAlign: 'center',
    },
  });
}

function useCardStyles() {
  const scale = useScale();

  return StyleSheet.create({
    playerContainer: {
      flex: 1,
      justifyContent: 'space-between',
    },
    contentArea: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    videoWrapper: {
      height: '100%',
      aspectRatio: 9 / 16,
      borderRadius: 16 * scale,
      overflow: 'hidden',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    video: {
      width: '100%',
      height: '100%',
    },
    noVideoPlaceholder: {
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
    },
    placeholderText: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 20 * scale,
      fontWeight: '600',
    },
    infoArea: {
      alignItems: 'center',
      paddingVertical: 8 * scale,
      gap: 4 * scale,
    },
    changeSidesText: {
      color: '#FFD667',
      fontSize: 18 * scale,
      fontWeight: '700',
    },
    repsTimeLabel: {
      fontSize: 40 * scale,
      lineHeight: 48 * scale,
      fontWeight: '700',
      color: '#fff',
    },
    nextUpText: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    progressText: {
      fontSize: 14 * scale,
      color: 'rgba(255, 255, 255, 0.6)',
    },
    bottomBar: {
      paddingHorizontal: 32 * scale,
      paddingBottom: 24 * scale,
      gap: 16 * scale,
    },
    controls: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 16 * scale,
    },
    controlButton: {
      width: 48 * scale,
      height: 48 * scale,
      borderRadius: 24 * scale,
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderWidth: 2,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    controlButtonLarge: {
      width: 56 * scale,
      height: 56 * scale,
      borderRadius: 28 * scale,
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
    controlButtonFocused: {
      borderColor: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
    },
  });
}
